import { Bounds } from "../engine/space/bounds";
import { ButtonComp } from "./input/Button";
import { Panel } from "./panels/Panel";
import { RDisplay } from "./RotDisplay";

export type MouseEvents = {
  press: (p: [number, number]) => void;
  release: (p: [number, number]) => void;
  hover: (p: [number, number]) => void;
  out: () => void;
};

export type KeyEvents = {
  keypress: (key: string, shift: boolean) => boolean;
};

export type UIEvents = Partial<MouseEvents & KeyEvents>;

export function bound<
  T extends { x: number; y: number; h?: number; w?: number }
>(o: Bounds, t: T): T {
  const result: any = { x: o.x + t.x, y: o.y + t.y };
  if (t.w != null) {
    result.w = t.w;
    result.h = t.h;
  }
  return result;
}

export function boundsAreSame(o: Bounds, t: Bounds) {
  return o.x === t.x && o.y === t.y && o.w === t.w && o.h === t.h;
}

export const isOver = (p: [number, number], b: Bounds) =>
  b.x <= p[0] && p[0] < b.x + b.w && b.y <= p[1] && p[1] < b.y + b.h;

export const LayerComp: ComponentF<any> = () => {
  return [
    createElement(
      Panel,
      { x: 0, y: 0, w: 29, h: 29 },
      { children: [createElement(ButtonComp, { x: 2, y: 2, w: 5, h: 5 })] }
    ),
  ];
};

export type ContextO = {
  display: RDisplay;
  bounds: Bounds;
  handlers: UIEvents;
  useMount: (f: () => void) => void;
  useState: (
    initialValue: any
  ) => readonly [
    Record<string, any>,
    (value: Record<string, any>) => Record<string, any>
  ];
};

export type ComponentO = {
  (display: RDisplay, bounds?: Bounds, prevState?: Record<string, any>): void;
  getChildren: () => ComponentO[];
  setChildren: (children: ComponentO[]) => void;
  getState: () => Record<string, any>;
  type: ComponentF<any>;
  bounds: Bounds;
  handlers: UIEvents;
};

export type ComponentF<P> = (C: ContextO, props: P) => ComponentO[] | void;

export const createElement = (
  el: ComponentF<any>,
  bounds: Bounds,
  props: any = {}
): ComponentO => {
  let initialized = false;

  let state = {};
  const setState = (value: Record<string, any>) => Object.assign(state, value);
  const useState = (initialValue: any) => {
    if (!initialized) {
      Object.assign(state, initialValue);
      initialized = true;
    }
    return [state, setState] as const;
  };
  const handlers = {};

  let mounted = false;
  const useMount = (f: () => void) => {
    if (!mounted) {
      mounted = true;
      f();
    }
  };
  let children: ComponentO[] = [];
  const r = (
    display: RDisplay,
    origin?: Bounds,
    prevState?: Record<string, any>
  ) => {
    if (prevState) {
      initialized = true;
      state = prevState;
    }
    const worldBounds = origin ? bound(origin, bounds) : bounds;
    const newChildren =
      el(
        {
          useMount,
          useState,
          display,
          handlers,
          bounds: worldBounds,
        },
        props
      ) || [];
    newChildren.forEach((child, index) => {
      if (index < children.length && child.type === children[index].type) {
        child.setChildren(children[index].getChildren());
        child(display, worldBounds, children[index].getState());
      } else {
        child(display, worldBounds);
      }
    });
    children = newChildren;
  };

  const getState = () => state;
  const setChildren = (c: ComponentO[]) => (children = c);
  const getChildren = () => children;

  return Object.assign(r, {
    type: el,
    getChildren,
    setChildren,
    getState,
    bounds,
    handlers,
  });
};

export const focusComponent = (root: ComponentO, current: ComponentO) => {
  const children = [...root.getChildren()];
  while (children.length) {
    const child = children.pop()!;
    if (child.getState().uiFocus) {
      child.getState().uiFocus = false;
      current.getState().uiFocus = true;
    }
    const newChildren = child?.getChildren();
    children.push(...newChildren);
  }
};
